import { computed, inject, Signal, Type } from '@angular/core';

import { SchemaActionsHost } from '../action/schema-action';
import { ComponentNode } from '../model/component-node';
import { SchemaActionsMap } from '../model/schema-action.types';
import { resolveSchemaComponent } from './resolve-schema-component';
import { SchemaComponentBaseComponent } from './schema-component-base.component';

/**
 * A node rendered once per value — a list row, where every row shares one blueprint and differs only in
 * data. It carries less than a {@link SchemaNode} on purpose: `states`, `dataSource` and `query` are
 * unreachable here, because a repeated node is built from a value that is already loaded, so
 * `deriveSchemaViewState` can only ever answer `content`.
 *
 * `actionsMap` is the one node key beyond {@link ComponentNode} that does mean something, and it means it
 * once for every row: which declaration a row's slot runs is a property of the blueprint, not of the row.
 */
export interface SchemaRepeatedNode<TConfig = unknown> extends ComponentNode<TConfig> {
  actionsMap?: SchemaActionsMap;
}

/** What a repeater needs to render one value: the resolved component and the config every row shares. */
export interface SchemaRepeatedNodeRef {
  readonly component: Signal<Type<SchemaComponentBaseComponent> | null>;
  readonly config: Signal<unknown>;
}

/**
 * Resolves a node that will be rendered many times — **once**, not once per value.
 *
 * `kros-schema-node-outlet` is the wrong tool for this. It is built for one node with one state and the
 * whole state machine around it, so repeating it costs, per row, five effects, a `resource` and three host
 * services, all of them re-deriving the same constant. The distinction worth keeping: *many nodes* is an
 * outlet each (a carousel's slides), *one node with many values* is this.
 *
 * The caller keeps its own markup — a `<ul>`/`<li>` here, bare rows there — so this is a function rather
 * than a component, and renders nothing itself:
 *
 * ```ts
 * @Component({ providers: [SchemaActionsHost], … })
 * export class ListViewComponent … {
 *   protected readonly item = injectSchemaRepeatedNode(computed(() => this.config()?.item));
 * }
 * ```
 *
 * ```html
 * @for (row of rows(); track $index) {
 *   <kros-schema-row-outlet [item]="item" [state]="row" />
 * }
 * ```
 *
 * `kros-schema-row-outlet` renders each row and makes it a place of its own, so an action fired from a row reads the
 * row, not the list (`SchemaRowOutletComponent`).
 *
 * **`providers: [SchemaActionsHost]` is required, not optional** — this throws at construction without it,
 * rather than falling back to the ambient host, which belongs to the node the outlet rendered and already
 * carries that node's own map. One host serves every row, which is the right number since they share one
 * map, and declarations still merge from the node above, so a swipe declared on the list keeps reaching
 * every row. §7 of `json-schemas/README-TECH.md` has the reasoning in full.
 */
export function injectSchemaRepeatedNode(node: Signal<SchemaRepeatedNode | undefined>): SchemaRepeatedNodeRef {
  /**
   * `self` rather than plain injection: without a host of its own the map would be written to the host of
   * the node doing the repeating, re-pointing that node's slots and every descendant's with it. That is a
   * wiring bug with no symptom at the point it is made, so it is worth refusing to start.
   */
  const actionsHost = inject(SchemaActionsHost, { self: true, optional: true });

  if (!actionsHost) {
    throw new Error(
      'injectSchemaRepeatedNode() needs a SchemaActionsHost of its own: add `providers: [SchemaActionsHost]` ' +
        'to the component that repeats the node. Without it the row map lands on the repeating node and is ' +
        'inherited by its whole subtree.'
    );
  }

  actionsHost.registerMap(computed(() => node()?.actionsMap));

  /**
   * Once for the whole list, which is the point: {@link resolveSchemaComponent} re-runs only when the key
   * or the preset changes, so editing the node's `config` does not resolve the component again.
   */
  const component = resolveSchemaComponent(
    computed(() => node()?.componentType),
    computed(() => node()?.preset)
  );

  return {
    component,
    config: computed(() => node()?.config),
  };
}
